import {useState} from 'react';
import {DatePicker} from 'antd';
import moment from 'moment';
import styles from './dashboard.module.scss';

/**
 * The date range control shared by every tab that takes one.
 *
 * Four tabs each carried their own copy of "Start Date / End Date / Apply",
 * and each computed its own header label like this:
 *
 *   const start = form.getFieldValue('startDate') || moment().startOf('month')
 *
 * Reading a field value during render is not reactive, so the label only caught
 * up when something else happened to re-render — and it claimed "01 Aug - 28
 * Aug" while the pickers sat empty and the query was actually unfiltered. The
 * range now lives in state, so what the header says is what was sent.
 *
 * The presets exist because every question this dashboard answers is asked
 * about a period, and setting two datetimes by hand to ask "how did last month
 * go" is a lot of clicks for the most common question on the page.
 */

const PRESETS = [
  {
    key: 'MTD',
    label: 'This month',
    range: () => [moment().startOf('month'), moment()],
  },
  {
    key: 'D30',
    label: 'Last 30 days',
    range: () => [moment().subtract(29, 'days').startOf('day'), moment()],
  },
  {
    key: 'LAST_MONTH',
    label: 'Last month',
    range: () => [
      moment().subtract(1, 'month').startOf('month'),
      moment().subtract(1, 'month').endOf('month'),
    ],
  },
  {
    key: 'YTD',
    label: 'This year',
    range: () => [moment().startOf('year'), moment()],
  },
  {key: 'ALL', label: 'All time', range: () => [null, null]},
];

/** The label for a range, used in section headers so scope travels with data. */
export const describeRange = ({startDate, endDate} = {}) => {
  if (!startDate && !endDate) return 'All time';
  const start = startDate
    ? moment(startDate).format('D MMM YYYY')
    : 'the start';
  const end = endDate ? moment(endDate).format('D MMM YYYY') : 'now';
  return `${start} – ${end}`;
};

export const DateRangeFilter = ({value, onChange, loading}) => {
  const [activePreset, setActivePreset] = useState('ALL');
  const [draft, setDraft] = useState([null, null]);

  const applyPreset = preset => {
    const [start, end] = preset.range();
    setActivePreset(preset.key);
    setDraft([start, end]);
    onChange({
      startDate: start ? start.format() : null,
      endDate: end ? end.format() : null,
    });
  };

  const applyCustom = range => {
    const [start, end] = range ?? [null, null];
    setDraft([start, end]);
    setActivePreset(start || end ? 'CUSTOM' : 'ALL');
    onChange({
      startDate: start ? moment(start).format() : null,
      endDate: end ? moment(end).format() : null,
    });
  };

  return (
    <div className={styles.rangeBar}>
      <div className={styles.presets} role="group" aria-label="Date range">
        {PRESETS.map(preset => (
          <button
            key={preset.key}
            type="button"
            disabled={loading}
            className={`${styles.preset} ${
              activePreset === preset.key ? styles.presetActive : ''
            }`}
            onClick={() => applyPreset(preset)}>
            {preset.label}
          </button>
        ))}
      </div>

      <DatePicker.RangePicker
        className={styles.rangePicker}
        value={draft}
        onChange={applyCustom}
        disabled={loading}
        allowEmpty={[true, true]}
        disabledDate={current => current && current.isAfter(moment(), 'day')}
        placeholder={['Custom start', 'Custom end']}
      />

      <span className={styles.rangeLabel}>{describeRange(value)}</span>
    </div>
  );
};

export default DateRangeFilter;
